"use client";

import { useState, Fragment, useRef, useEffect, ReactNode } from "react";
import { useLocale } from "@/components/LocaleProvider";
import { createPortal } from "react-dom";
import { ImageSlideshow, type SlideImage } from "@/components/ImageSlideshow";

interface Note {
  year: string
  title: string
  description: string
  type: string
  link?: string
  linkLabel?: string
  /** Optional: short context (What / Who). */
  context?: string
  /** Optional: focus or approach. */
  focus?: string
  /** Optional: outcome or status. */
  outcome?: string
}

const notes: Note[] = [
  {
    year: '2025',
    title: 'TILLYZ: AI-Powered Vision & Mobile Expense Engineering',
    description: 'Engineering a high-efficiency Video - OCR framework to eliminate manual data entry and "Filter Fatigue" in expense tracking.',
    type: 'Concept · Exploration · Ongoing',
    link: 'https://tillyz.netlify.app',
    linkLabel: 'Tillyz V1',
    context: 'A technical exploration leveraging real-time video stream sampling and AI parsing to transform unstructured receipt data into organized, actionable financial insights.',
    focus: 'Minimizing interaction cost through video-based capture, deterministic AI data-mapping, and one-handed mobile ergonomics.',
    outcome: 'Ongoing  -  Functional prototype successfully reducing capture friction by ~65%; refining AI categorization and relational data logic.',
  },
  {
    year: '2025',
    title: 'PRINTILATOR: 3D Print Quoting & Workflow System',
    description: 'Engineering an Enterprise-Grade SaaS for precision manufacturing, transforming complex variables into actionable business insights.',
    type: 'Concept · Exploration · Ongoing',
    link: 'https://printilator.netlify.app',
    linkLabel: 'Printilator V1',
    context: 'An industrial-grade tool designed for 3D print shop owners to manage filament stock, machine time, and multi-factor cost estimations in real-time.',
    focus: 'Solving "Estimation Drift" through reactive calculation engines, mobile-to-desktop responsiveness, and B2B monetization logic.',
    outcome: 'Ongoing  -  Advanced prototype featuring a full React/Supabase stack, Data Resilience & Compliance features, and an In-App Promotion Engine.',
},
]

// UI screenshots: contain + padding keeps aspect ratio (no crop/stretch like cover).
// unoptimized serves the file as-is; large sizes hint helps if optimization is on later.
const tillyzSlideStyle = {
  containerClassName: "p-3 sm:p-4",
  imgClassName:
    "object-center min-h-0 min-w-0 max-h-full max-w-full [image-rendering:auto]",
  unoptimized: true,
  sizes: "(max-width: 1024px) 92vw, min(900px, 48vw)",
} as const;

const tillyzSlidesBySection: Record<number, SlideImage[]> = {
  0: [
    {
      src: "/images/tillyz/damen-oosthuizen-tillyz-mobile-dashboard.webp",
      alt: "TILLYZ mobile dashboard",
      ...tillyzSlideStyle,
    },
  ],
  1: [
    {
      src: "/images/tillyz/damen-oosthuizen-tillyz-raw-data.webp",
      alt: "TILLYZ data layer and mapped expense fields",
      ...tillyzSlideStyle,
    },
  ],
  2: [
    {
      src: "/images/tillyz/damen-oosthuizen-tillyz-supabase-table.webp",
      alt: "TILLYZ Supabase table schema and relational data",
      ...tillyzSlideStyle,
      containerClassName: "p-0",
      imgClassName:
        "object-cover object-center min-h-0 min-w-0 max-h-full max-w-full [image-rendering:auto]",
    },
  ],
  3: [
    {
      src: "/images/tillyz/damen-oosthuizen-tillyz-spend-by-category.webp",
      alt: "TILLYZ spend by category",
      ...tillyzSlideStyle,
    },
  ],
};

const printilatorSlidesBySection: Record<number, SlideImage[]> = {
  0: [
    {
      src: "/images/printilator/damen-oosthuizen-printilator-engineering-precision.webp",
      alt: "Printilator - Engineering precision for 3D manufacturing quotes",
      ...tillyzSlideStyle,
    },
  ],
  1: [
    {
      src: "/images/printilator/damen-oosthuizen-printilator-upmarket-aesthetics.webp",
      alt: "Printilator Industrial UI and Quote Workflow",
      ...tillyzSlideStyle,
    },
  ],
  2: [
    {
      src: "/images/printilator/damen-oosthuizen-printilator-full-stack-architecture.webp",
      alt: "Printilator Full-Stack Architecture",
      ...tillyzSlideStyle,
    },
  ],
  3: [
    {
      src: "/images/printilator/damen-oosthuizen-printilator-measurable-outcomes.webp",
      alt: "Printilator Outcomes and Roadmap Metrics",
      ...tillyzSlideStyle,
    },
  ],
};

const CARD_BORDER_RADIUS = 8;

export default function Writing() {
  const { t } = useLocale();
  const [selectedNote, setSelectedNote] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [openAccordions, setOpenAccordions] = useState<Set<number>>(new Set());
  const [cardDimensions, setCardDimensions] = useState<Record<number, { width: number; height: number }>>({});
  const [hoveredNoteIndex, setHoveredNoteIndex] = useState<number | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const refs = cardRefs.current;
    const observers: ResizeObserver[] = [];
    notes.forEach((_, index) => {
      const el = refs[index];
      if (!el) return;
      const ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const el = entry.target as HTMLElement;
        const { width, height } = el.getBoundingClientRect();
        setCardDimensions((prev) => ({
          ...prev,
          [index]: { width: Math.max(1, width), height: Math.max(1, height) },
        }));
      });
      ro.observe(el);
      observers.push(ro);
    });
    return () => observers.forEach((ro) => ro.disconnect());
  }, []);

  const openNote = (index: number) => {
    setSelectedNote(index);
    setIsClosing(false);
    setOpenAccordions(new Set([0]));
    document.body.style.overflow = "hidden";
  };

  const closeNote = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedNote(null);
      setIsClosing(false);
      setOpenAccordions(new Set());
      document.body.style.overflow = "";
    }, 500);
  };

  const toggleAccordion = (index: number, isLastSection: boolean) => {
    setOpenAccordions((prev) => {
      if (prev.has(index)) {
        if (isLastSection) return prev;
        return new Set();
      }
      return new Set([index]);
    });
  };

  const renderAccordionSection = (
    sectionIndex: number,
    title: string,
    content: ReactNode,
    isLast: boolean = false,
    rightContent?: ReactNode,
    tallImagePanel?: boolean
  ) => {
    const isOpen = openAccordions.has(sectionIndex);
    return (
      <div
        key={sectionIndex}
        className={`flex flex-col md:flex-row relative md:h-full ${
          isLast ? "" : "border-b md:border-b-0 border-black/10 dark:border-white/10"
        } transition-all duration-300 ease-in-out bg-background ${
          isOpen ? "flex-1" : "md:w-[80px] md:flex-shrink-0"
        }`}
      >
        {!isLast && (
          <div className="hidden md:block absolute right-0 top-0 bottom-0 w-[1px] bg-black/10 dark:bg-white/10 pointer-events-none" />
        )}
        {!isOpen ? (
          <button
            onClick={() => toggleAccordion(sectionIndex, isLast)}
            className="cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 w-full md:w-[80px] md:h-full flex items-start justify-start py-6 md:py-12 px-4 md:px-6 transition-all duration-300 ease-in-out bg-background"
            aria-expanded={isOpen}
            aria-controls={`note-accordion-content-${sectionIndex}`}
          >
            <h3 className="font-bold text-foreground text-base md:text-lg md:whitespace-nowrap md:[writing-mode:vertical-rl] md:[text-orientation:mixed] md:transform md:rotate-180">
              {title}
            </h3>
          </button>
        ) : (
          <div className="flex flex-col md:flex-row flex-1 md:h-full">
            <button
              onClick={() => toggleAccordion(sectionIndex, isLast)}
              className={`flex-shrink-0 w-full md:w-[80px] flex items-start justify-start py-6 md:py-12 px-4 md:px-6 transition-all duration-300 ease-in-out bg-background ${
                isLast
                  ? "cursor-default"
                  : "hover:bg-black/5 dark:hover:bg-white/5"
              }`}
              aria-expanded={isOpen}
              aria-controls={`note-accordion-content-${sectionIndex}`}
            >
              <h3 className="font-bold text-foreground text-base md:text-lg md:whitespace-nowrap md:[writing-mode:vertical-rl] md:[text-orientation:mixed] md:transform md:rotate-180">
                {title}
              </h3>
            </button>
            <div
              id={`note-accordion-content-${sectionIndex}`}
              className="flex-1 flex flex-col md:flex-row gap-4 md:gap-6 overflow-visible md:overflow-hidden min-h-0 py-6 md:py-12 px-4 md:px-6"
            >
              <div className="flex-1 min-h-0 text-[15px] text-foreground/70 leading-relaxed overflow-visible md:overflow-y-auto scrollbar-hide md:pr-4 text-left">
                {content}
              </div>
              {rightContent && (
                <div className="w-full md:w-1/2 flex-shrink-0 min-h-[260px] overflow-visible md:overflow-y-auto overflow-x-hidden md:pr-2">
                  <div
                    className={
                      tallImagePanel
                        ? "w-full h-full min-h-[260px] md:min-h-[min(72vh,640px)]"
                        : "w-full h-full min-h-[260px]"
                    }
                  >
                    {rightContent}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (selectedNote === null) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeNote();
    };

    const handleTab = (e: KeyboardEvent) => {
      if (!modalRef.current) return;
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("keydown", handleTab);
    setTimeout(() => closeButtonRef.current?.focus(), 100);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleTab);
    };
  }, [selectedNote]);

  return (
    <Fragment>
      <section id="notes" className="scroll-mt-12 pt-14 pb-14 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-[3em]">
        <h2 className="inline-block text-sm font-bold font-montserrat uppercase bg-[#333333] text-white dark:bg-white dark:text-black px-3 py-1.5 mb-5">{t("sections.explorations")}</h2>
        <p className="text-[15px] text-foreground/70 mb-12 leading-relaxed">
          {t("sections.explorationsSubtitle")}
        </p>
        <div className="space-y-12">
          {notes.map((note, index) => {
            const dims = cardDimensions[index];
            const vbW = dims?.width ?? 100;
            const vbH = dims?.height ?? 100;
            const isHovered = hoveredNoteIndex === index;
            return (
            <div
              key={index}
              ref={(el) => { cardRefs.current[index] = el; }}
              onClick={() => openNote(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openNote(index);
                }
              }}
              onMouseEnter={() => setHoveredNoteIndex(index)}
              onMouseLeave={() => setHoveredNoteIndex(null)}
              onFocus={() => setHoveredNoteIndex(index)}
              onBlur={() => setHoveredNoteIndex(null)}
              tabIndex={0}
              role="button"
              className="group cursor-pointer relative flex flex-col md:flex-row gap-8 rounded-lg p-4 -m-4 border border-transparent transition-all duration-200 focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2"
            >
              {/* Line-draw border (same as View Full Résumé)  -  keeps card shape (rounded-lg) */}
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full overflow-visible rounded-lg text-[#e5e5e5] dark:text-[#ffffff1a]"
                viewBox={`0 0 ${vbW} ${vbH}`}
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <rect
                  x={1}
                  y={1}
                  width={Math.max(0, vbW - 2)}
                  height={Math.max(0, vbH - 2)}
                  rx={CARD_BORDER_RADIUS}
                  ry={CARD_BORDER_RADIUS}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  pathLength={1}
                  className={`border-draw-path ${isHovered ? "animate-border-draw" : ""}`}
                />
              </svg>
              <div className="md:w-20 flex-shrink-0 relative z-[1]">
                <span className="text-sm font-normal text-foreground/70">
                  {note.year}
                </span>
              </div>
              <div className="flex-1 relative z-[1] flex flex-col">
                <h3 id={`note-card-title-${index}`} className="order-1 text-lg font-bold text-foreground mb-2">
                  {t(`notes.${index}.title`)}
                </h3>
                <ul className="order-2 md:order-3 flex flex-wrap gap-2 mb-3">
                  {t(`notes.${index}.type`).split('·').map((typeItem, typeIndex) => {
                    const trimmedType = typeItem.trim()
                    return trimmedType ? (
                      <li
                        key={typeIndex}
                        className="px-3 py-1 text-xs rounded-full border border-black/15 dark:border-white/20 max-md:bg-white max-md:text-neutral-800 md:bg-black/5 md:text-foreground/70 dark:max-md:bg-white dark:max-md:text-neutral-900 dark:md:bg-white/5"
                      >
                        {trimmedType}
                      </li>
                    ) : null
                  })}
                </ul>
                <p className="order-3 md:order-2 text-[15px] text-foreground/70 mb-3 leading-relaxed">
                  {t(`notes.${index}.description`)}
                </p>
                 {note.link && (
                  <a
                    href={note.link}
                    target={note.link.startsWith('http') ? '_blank' : undefined}
                    rel={note.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    onClick={(e) => e.stopPropagation()}
                    className="order-4 text-[15px] font-medium text-black dark:text-white hover:opacity-70 transition-opacity inline-flex items-center gap-1.5 py-2 focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded uppercase"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    {t(`notes.${index}.linkLabel`)} →
                    {note.link.startsWith('http') ? (
                      <span className="sr-only"> ({t('common.opensInNewWindow')})</span>
                    ) : null}
                  </a>
                )} 
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {selectedNote !== null &&
        notes[selectedNote] &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="note-modal-title"
            ref={modalRef}
          >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeNote}
            aria-hidden="true"
          />
          <div
            className={`absolute inset-0 bg-background ${
              isClosing ? "animate-slide-out-to-right" : "animate-slide-in-from-right"
            }`}
          >
            <div className="h-full flex flex-col overflow-hidden">
              {/* Header: back, title, close in one bar */}
              <div className="flex-shrink-0 border-b border-black/10 dark:border-white/10 px-4 md:px-6 py-4 md:py-4 pt-[calc(env(safe-area-inset-top)+1rem)] md:pt-6 flex items-center gap-3 min-h-[56px]">
                <button
                  ref={closeButtonRef}
                  onClick={closeNote}
                  className="flex-shrink-0 text-black dark:text-white hover:opacity-70 transition-opacity flex items-center gap-2 min-h-[44px] min-w-[44px] focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                  aria-label={t("modal.closeNoteDetails")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2
                  id="note-modal-title"
                  className="flex-1 min-w-0 text-base sm:text-lg font-bold text-foreground font-montserrat uppercase text-left leading-tight truncate"
                >
                  {t(`notes.${selectedNote}.title`)}
                </h2>
                <button
                  onClick={closeNote}
                  className="flex-shrink-0 text-black dark:text-white hover:opacity-70 transition-opacity min-h-[44px] min-w-[44px] flex items-center justify-center focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                  aria-label={t("modal.close")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-hidden overflow-y-auto scrollbar-hide">
                {selectedNote !== null && notes[selectedNote] && (() => {
                  const note = notes[selectedNote];
                  const hasLink = !!note.link;
                  const isTillyz = selectedNote === 0;
                  const isPrintilator = selectedNote === 1;

                  // TILLYZ: use Projects-style 4-tab modal
                  if (isTillyz) {
                    return (
                      <div className="h-full flex flex-col md:flex-row">
                        {renderAccordionSection(
                          0,
                          t("modal.contextAndProblem"),
                          <div className="mb-4 space-y-6">
                            <h3 className="text-lg font-semibold mb-4">
                              Reimagining Expense Capture via Video - OCR
                            </h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">The Brief:</strong>
                              <p>
                                Designed as a high-efficiency mobile tool for individuals and
                                small teams, TILLYZ explores how lightweight OCR (Optical
                                Character Recognition) and structured data can eliminate the
                                manual burden of expense tracking. The goal was to transform
                                the &quot;shoebox of receipts&quot; into a real-time data
                                stream with minimal user friction.
                              </p>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">The Problem</h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                The &quot;Input Barrier&quot;:
                              </strong>
                              <p>
                                Standard expense apps often fail because the &quot;Interaction
                                Cost&quot; of capturing a receipt-taking a still photo,
                                waiting for a slow scan, and correcting errors is too high.
                                Users need a &quot;micro-moment&quot; solution that works in
                                the seconds they have available while on the go.
                              </p>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">The Decision Moment</h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                From Photo to Video Stream:
                              </strong>
                              <p>
                                Initial testing showed that static photos were often blurry or
                                poorly framed, leading to OCR failure. I made the strategic
                                pivot to <strong>Video-Based Capture</strong>. By recording a
                                short video stream, we could sample multiple frames to find
                                the clearest text, significantly increasing data accuracy and
                                reducing user frustration.
                              </p>
                            </div>
                          </div>,
                          false,
                          <ImageSlideshow
                            images={tillyzSlidesBySection[0]}
                            emptyLabel={t("modal.contextImages")}
                          />,
                          true
                        )}
                        {renderAccordionSection(
                          1,
                          t("modal.uxStrategy"),
                          <div className="mb-4 space-y-6">
                            <h3 className="text-lg font-semibold mb-4">
                              Mobile-First Efficiency &amp; Behavioural Logic
                            </h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                Applying Lean UX &amp; Mobile Patterns:
                              </strong>
                              <p>
                                Since the user&apos;s primary goal was
                                &quot;speed-to-insight,&quot; I prioritized a mobile-first
                                architecture optimized for one-handed use.
                              </p>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">
                              Key Design Decisions
                            </h3>
                            <div className="space-y-3">
                              <div>
                                <strong className="block mb-1.5">
                                  The &quot;Zero-Waste&quot; Workflow:
                                </strong>
                                <p>
                                  Designed a hierarchy where store, item, and category
                                  creation are decoupled but intelligently linked. If a user
                                  adds a brand, the system suggests the corresponding category
                                  automatically.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">
                                  Editable Integrity:
                                </strong>
                                <p>
                                  Acknowledging that OCR is never 100% perfect, I engineered a
                                  UI where <strong>all amounts and tax calculations are immediately
                                  editable</strong>, ensuring the user always feels in control of their
                                  financial data.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">
                                  Frictionless Authentication:
                                </strong>
                                <p>
                                  Implemented Google OAuth via Supabase to ensure users could
                                  move from &quot;App Launch&quot; to &quot;Receipt Scan&quot;
                                  in under three seconds.
                                </p>
                              </div>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">The Process</h3>
                            <div className="space-y-3">
                              <strong className="block mb-1.5">
                                Rapid Prototyping:
                              </strong>
                              <p>
                                Built using React and Supabase, allowing for a &quot;Live-Data&quot; prototype that
                                stakeholders could test on their actual devices immediately.
                              </p>
                              <strong className="block mb-1.5">
                                Metric-Driven Visualization:
                              </strong>
                              <p>
                                Developed a dashboard logic that aggregates spend by month and category,
                                translating raw data into actionable financial insights.
                              </p>
                            </div>
                          </div>,
                          false,
                          <ImageSlideshow
                            images={tillyzSlidesBySection[1]}
                            emptyLabel={t("modal.uxStrategyImages")}
                          />,
                          true
                        )}
                        {renderAccordionSection(
                          2,
                          t("modal.technicalExecution"),
                          <div className="mb-4 space-y-6">
                            <h3 className="text-lg font-semibold mb-4">
                              Full-Stack Engineering with React &amp; Supabase
                            </h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                The Technical Stack:
                              </strong>
                              <p>
                                To deliver a production-ready concept, I utilized a modern
                                &quot;BaaS&quot; (Backend-as-a-Service) architecture. This
                                allowed me to focus on the complex front-end logic of OCR
                                processing and real-time state management.
                              </p>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">
                              Technical Highlights
                            </h3>
                            <div className="space-y-3">
                              <div>
                                <strong className="block mb-1.5">
                                  Video Frame Sampling:
                                </strong>
                                <p>
                                  Instead of a single static image, the system analyzes a
                                  short video buffer to extract the highest-quality text data,
                                  a significant upgrade over traditional
                                  &quot;Scan-and-Wait&quot; models.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">
                                  Relational Data Mapping:
                                </strong>
                                <p>
                                  Engineered a flexible database schema in Supabase that
                                  handles many-to-many relationships between Stores, Items,
                                  and Categories, allowing for deep-dive spend analysis.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">Dynamic Tax Logic:</strong>
                                <p>
                                  Built a custom calculation engine that applies tax logically
                                  across different item types, ensuring the data is
                                  &quot;Accounting-Ready&quot; upon export.
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                The UX Engineering Advantage:
                              </strong>
                              <p>
                                By handling both the React implementation and the Database
                                architecture, I ensured the technical constraints of OCR never
                                compromised the user experience. I delivered a functional,
                                high-fidelity prototype that proves the viability of
                                video-assisted data entry.
                              </p>
                            </div>
                          </div>,
                          false,
                          <ImageSlideshow
                            images={tillyzSlidesBySection[2]}
                            emptyLabel={t("modal.technicalExecutionImages")}
                            whiteBackground
                          />,
                          true
                        )}
                        {renderAccordionSection(
                          3,
                          t("modal.impactAndRoadmap"),
                          <div className="mb-4 space-y-6">
                            <h3 className="text-lg font-semibold mb-4">
                              Measurable Outcomes (Modelled) &amp; Evolution
                            </h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                Measurable Outcomes (Modelled):
                              </strong>
                              <ul className="list-disc list-inside space-y-2 mt-2">
                                <li>
                                  <strong>65% Reduction in Capture Time:</strong> Transitioning
                                  from manual entry to video - OCR is modelled to save users over
                                  a minute per receipt.
                                </li>
                                <li>
                                  <strong>Higher Data Accuracy:</strong> Video-frame sampling
                                  significantly reduces &quot;Empty&quot; or &quot;Garbage&quot; OCR
                                  results compared to static photo capture.
                                </li>
                                <li>
                                  <strong>Immediate Value Realization:</strong> Users reach the
                                  &quot;Insight&quot; phase (monthly spend totals) with 40%
                                  fewer interactions than legacy expense apps.
                                </li>
                              </ul>
                            </div>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                Current Status &amp; Roadmap:
                              </strong>
                              <p>
                                TILLYZ is currently an early-stage functional prototype. The
                                roadmap includes refining the OCR engine for low-light
                                environments and expanding the category suggestion algorithm
                                into a full-scale machine learning model. This project
                                demonstrates my ability to take a vague user need and turn it
                                into a technical proof-of-concept that solves a real-world
                                problem.
                              </p>
                            </div>
                          </div>,
                          true,
                          <ImageSlideshow
                            images={tillyzSlidesBySection[3]}
                            emptyLabel={t("modal.impactImages")}
                            whiteBackground
                          />,
                          true
                        )}
                      </div>
                    );
                  }

                  // PRINTILATOR: use Projects-style 4-tab modal
                  if (isPrintilator) {
                    return (
                      <div className="h-full flex flex-col md:flex-row">
                        {renderAccordionSection(
                          0,
                          t("modal.contextAndProblem"),
                          <div className="mb-4 space-y-6">
                            <h3 className="text-lg font-semibold mb-4">
                              PRINTILATOR: Engineering a Precision OS for 3D Manufacturing
                            </h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">The Brief:</strong>
                              <p>
                                Small-scale 3D print shops often struggle with
                                &quot;Estimation Drift&quot;  -  the gap between a quoted price
                                and the actual cost of materials, machine time, and overhead.
                                I engineered Printilator as a cross-platform (Mobile/Desktop)
                                solution to surface these complex variables in a clear,
                                actionable way, allowing shop owners to generate precise
                                invoices on the fly.
                              </p>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">The Problem</h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                The Complexity of &quot;Small-Batch&quot; Costing:
                              </strong>
                              <p>
                                Calculating a 3D print quote involves managing fragmented
                                data: filament density, varying electricity rates, machine
                                wear-and-tear, and labour. Most shop owners rely on
                                spreadsheets that are difficult to navigate on mobile and
                                prone to manual error. The goal was to build a system that
                                made multi-factor costing understandable without
                                overwhelming the user.
                              </p>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">The Vision</h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                Scalable Business Logic:
                              </strong>
                              <p>
                                Beyond just a calculator, Printilator was designed with a
                                &quot;Freemium-to-Pro&quot; architecture. This included an
                                In-App Promotion Engine for admins to promote service
                                fast-tracking and a subscription-ready state for business
                                scaling.
                              </p>
                            </div>
                          </div>,
                          false,
                          <ImageSlideshow
                            images={printilatorSlidesBySection[0]}
                            emptyLabel={t("modal.contextImages")}
                          />,
                          true
                        )}
                        {renderAccordionSection(
                          1,
                          t("modal.uxStrategy"),
                          <div className="mb-4 space-y-6">
                            <h3 className="text-lg font-semibold mb-4">
                              Industrial UI: Precision, Speed, and Upmarket Aesthetics
                            </h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                Systematizing the &quot;Quote-to-Invoice&quot; Flow:
                              </strong>
                              <p>
                                I focused on an Enterprise-Grade, Upmarket aesthetic using
                                Tailwind CSS, ensuring the tool felt like professional
                                industrial software rather than a hobbyist utility.
                              </p>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">
                              Key Design Decisions
                            </h3>
                            <div className="space-y-3">
                              <div>
                                <strong className="block mb-1.5">
                                  Intelligent Defaults:
                                </strong>
                                <p>
                                  To accelerate the workflow, the system allows users to
                                  customize &quot;Base Criteria&quot; (preferred filaments/printers).
                                  This enables rapid data entry based on historical patterns.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">
                                  Responsive Utility:
                                </strong>
                                <p>
                                  Engineered for a &quot;Mobile-First, Desktop-Complete&quot;
                                  experience. Shop owners can check stock on the floor via
                                  mobile or generate detailed PDF invoices on a desktop.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">
                                  Visual Validation:
                                </strong>
                                <p>
                                  Integrated a visual display to showcase the item being
                                  rendered, providing immediate visual feedback that the
                                  correct specifications are being applied to the quote.
                                </p>
                              </div>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">The Process</h3>
                            <div className="space-y-3">
                              <strong className="block mb-1.5">
                                Variable Isolation:
                              </strong>
                              <p>
                                I mapped every cost factor (material weight, hourly machine
                                rate, energy consumption) to ensure the calculation engine
                                was logically sound and editable.
                              </p>
                              <strong className="block mb-1.5">
                                Feedback Loops:
                              </strong>
                              <p>
                                Built a &quot;Trial-to-Upgrade&quot; user flow, ensuring the
                                UI clearly communicated value before moving to a
                                subscription model.
                              </p>
                            </div>
                          </div>,
                          false,
                          <ImageSlideshow
                            images={printilatorSlidesBySection[1]}
                            emptyLabel={t("modal.uxStrategyImages")}
                          />,
                          true
                        )}
                        {renderAccordionSection(
                          2,
                          t("modal.technicalExecution"),
                          <div className="mb-4 space-y-6">
                            <h3 className="text-lg font-semibold mb-4">
                              Full-Stack Architecture with React &amp; Supabase
                            </h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                Building for Data Integrity:
                              </strong>
                              <p>
                                The system is built on React with Supabase as the backbone,
                                handling real-time data synchronization and secure Google
                                OAuth authentication.
                              </p>
                            </div>
                            <h3 className="text-lg font-semibold mb-4">
                              Technical Highlights
                            </h3>
                            <div className="space-y-3">
                              <div>
                                <strong className="block mb-1.5">
                                  Filament &amp; Stock Logic:
                                </strong>
                                <p>
                                  Engineered a management module for filament specifications,
                                  allowing for precise material-cost-per-gram calculations
                                  based on current stock levels.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">
                                  In-App Promotion Engine:
                                </strong>
                                <p>
                                  Built an internal marketing system that allows admins to
                                  showcase internal promotions or service fast-tracking to
                                  users upon login, facilitating a dual-revenue model.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">
                                  Data Resilience &amp; Compliance:
                                </strong>
                                <p>
                                  Implemented a manual backup system where database
                                  snapshots are automatically mailed to the user, alongside
                                  a global &quot;Export to CSV/PDF&quot; feature to ensure
                                  long-term data portability and resilience.
                                </p>
                              </div>
                              <div>
                                <strong className="block mb-1.5">
                                  Cross-Platform Readiness:
                                </strong>
                                <p>
                                  Optimized the codebase for potential Play Store listing,
                                  utilizing responsive design patterns that adapt to native
                                  mobile environments.
                                </p>
                              </div>
                            </div>
                          </div>,
                          false,
                          <ImageSlideshow
                            images={printilatorSlidesBySection[2]}
                            emptyLabel={t("modal.technicalExecutionImages")}
                          />,
                          true
                        )}
                        {renderAccordionSection(
                          3,
                          t("modal.impactAndRoadmap"),
                          <div className="mb-4 space-y-6">
                            <h3 className="text-lg font-semibold mb-4">
                              Measurable Outcomes &amp; Scaling the Shop Floor
                            </h3>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                Measurable Outcomes (Modelled):
                              </strong>
                              <ul className="list-disc list-inside space-y-2 mt-2">
                                <li>
                                  <strong>Quoting Accuracy:</strong> By automating
                                  multi-factor calculations, the system eliminates the
                                  10–15% &quot;Estimation Error&quot; common in manual
                                  quoting.
                                </li>
                                <li>
                                  <strong>Increased Velocity:</strong> Reducing
                                  time-to-invoice from minutes to seconds through the use
                                  of saved &quot;Printer Profiles&quot; and &quot;Filament
                                  Presets.&quot;
                                </li>
                                <li>
                                  <strong>Business Transparency:</strong> Providing users
                                  with clear &quot;Cost vs Profit&quot; breakdowns for
                                  every single print task. This enables data-driven pricing
                                  strategies rather than intuition-based quoting.
                                </li>
                              </ul>
                            </div>
                            <div className="space-y-2">
                              <strong className="block mb-1.5">
                                Current Status &amp; Roadmap:
                              </strong>
                              <p>
                                Printilator is currently in an advanced prototype stage.
                                The next phase of engineering involves:
                              </p>
                              <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
                                <li>
                                  <strong>Label Printing Integration:</strong> Generating
                                  machine-specific labels directly from the quote.
                                </li>
                                <li>
                                  <strong>Full Stock Management:</strong> Transitioning
                                  from &quot;Basic Addition&quot; to a full-scale inventory
                                  tracking system with low-stock alerts.
                                </li>
                                <li>
                                  <strong>Payment Finalization:</strong> Completing the
                                  integration for monthly subscription plans and direct
                                  client invoicing.
                                </li>
                              </ul>
                            </div>
                          </div>,
                          true,
                          <ImageSlideshow
                            images={printilatorSlidesBySection[3]}
                            emptyLabel={t("modal.impactImages")}
                          />,
                          true
                        )}
                      </div>
                    );
                  }

                  // Default: generic note modal
                  return (
                    <div className="h-full flex flex-col md:flex-row">
                      {renderAccordionSection(
                        0,
                        t("modal.context"),
                        <div className="mb-4 space-y-4">
                          <div>
                            <strong className="block mb-1">{t("modal.year")}:</strong>
                            <p>{note.year}</p>
                          </div>
                          <div>
                            <strong className="block mb-1">{t("modal.type")}:</strong>
                            <ul className="flex flex-wrap gap-2 mt-1">
                              {t(`notes.${selectedNote}.type`).split('·').map((typeItem, typeIndex) => {
                                const trimmedType = typeItem.trim();
                                return trimmedType ? (
                                  <li
                                    key={typeIndex}
                                    className="px-3 py-1 text-xs bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-full text-foreground/70"
                                  >
                                    {trimmedType}
                                  </li>
                                ) : null;
                              })}
                            </ul>
                          </div>
                          {note.context && (
                            <div>
                              <strong className="block mb-1">{t("modal.what")}:</strong>
                              <p>{t(`notes.${selectedNote}.context`)}</p>
                            </div>
                          )}
                        </div>,
                        false
                      )}
                      {renderAccordionSection(
                        1,
                        t("modal.summary"),
                        <div className="mb-4 space-y-4">
                          <p>{t(`notes.${selectedNote}.description`)}</p>
                        </div>,
                        false
                      )}
                      {renderAccordionSection(
                        2,
                        t("modal.focus"),
                        <div className="mb-4 space-y-4">
                          <p>{t(`notes.${selectedNote}.focus`) || t("modal.focusFallback")}</p>
                        </div>,
                        !hasLink
                      )}
                      {hasLink && renderAccordionSection(
                        3,
                        t("modal.explore"),
                        <div className="mb-4 space-y-4">
                          <p>
                            {t("modal.exploreIntro")}
                          </p>
                          <a
                            href={note.link}
                            target={note.link!.startsWith('http') ? '_blank' : undefined}
                            rel={note.link!.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="text-[15px] font-medium text-black dark:text-white hover:opacity-70 transition-opacity inline-flex items-center gap-1.5 py-2 focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2 rounded"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                            {t(`notes.${selectedNote}.linkLabel`)} →
                            {note.link!.startsWith('http') ? (
                              <span className="sr-only"> ({t('common.opensInNewWindow')})</span>
                            ) : null}
                          </a>
                        </div>,
                        false,
                        <div className="w-full h-full min-h-[260px] bg-gray-100 dark:bg-white/5 rounded flex flex-col items-center justify-center p-8 text-center">
                          <p className="text-[15px] text-foreground/70 mb-4">
                            {t("modal.openPrototype")}
                          </p>
                          <a
                            href={note.link}
                            target={note.link!.startsWith('http') ? '_blank' : undefined}
                            rel={note.link!.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="px-4 py-3 text-[15px] font-medium bg-black dark:bg-white text-white dark:text-black rounded hover:opacity-90 transition-opacity focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2"
                          >
                            {t(`notes.${selectedNote}.linkLabel`)} →
                            <span className="sr-only"> ({t('common.opensInNewWindow')})</span>
                          </a>
                        </div>
                      )}
                      {renderAccordionSection(
                        hasLink ? 4 : 3,
                        t("modal.outcome"),
                        <div className="mb-4 space-y-4">
                          <p>{t(`notes.${selectedNote}.outcome`) || t("modal.outcomeFallback")}</p>
                        </div>,
                        true
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>,
          document.body
        )}
    </Fragment>
  );
}
